import './formfield.scss'

import {DOMAttrs} from '@codeurs/front'
import {
	ErrorMessage,
	Field,
	FieldAttributes,
	useField,
	useFormikContext
} from 'formik'
import React, {
	FunctionComponent,
	HTMLProps,
	ReactElement,
	useEffect,
	useRef,
	useState
} from 'react'
import AnimateHeight from 'react-animate-height'
import {IMaskMixin} from 'react-imask'
import {ErrorIcon, SuccessIcon} from 'styles/inline-icons'
import {style} from 'util/campagne/style'
import {scrollTo} from 'util/scrollmemory'
import {Theme} from 'views/campagne/theme'

type InputAttrs =
	| ({component?: undefined} & HTMLProps<HTMLInputElement>)
	| ({component: 'select'} & HTMLProps<HTMLSelectElement>)
	| ({component: 'textarea'} & HTMLProps<HTMLTextAreaElement>)

export type FieldType = {error?: boolean} & Omit<InputAttrs, 'label'>

export const fieldStyles = style('field', ['checkbox', 'autocompleteitem'])

export const FormField: FunctionComponent<
	DOMAttrs & {
		icon?: ReactElement
		small?: boolean
		labelFlex?: 'column' | 'row'
	}
> = ({
	label,
	name,
	error,
	icon,
	labelFlex = 'column',
	small = false,
	textarea = false,
	info,
	type = textarea ? undefined : 'text',
	...attrs
}) => {
	const [_, meta, helpers] = useField(name)
	const showError = !!meta.error
	const element = useRef<HTMLDivElement>()
	useEffect(() => {
		if (showError) scrollTo(() => 0, element.current)
	}, [showError])
	return (
		<div class={fieldStyles} ref={element}>
			{label && labelFlex === 'column' && (
				<label htmlFor={name} class={fieldStyles('label').mod('column')}>
					{label}
				</label>
			)}
			{info && (
				<div class={fieldStyles('info')}>
					<Theme.P mod="small">{info} </Theme.P>
				</div>
			)}
			<div class={fieldStyles('container')}>
				{label && labelFlex === 'row' && (
					<label htmlFor={name} class={fieldStyles('label').mod('row')}>
						{label}
					</label>
				)}
				<div
					class={fieldStyles('input')
						.mod({label: labelFlex === 'row', small, textarea})
						.is({error: showError})}
				>
					<Field
						class={fieldStyles('input-value')}
						name={name}
						id={name}
						component={textarea && 'textarea'}
						rows={textarea && 24}
						type={type}
						{...attrs}
					/>
				</div>
				<ErrorIcon
					className={fieldStyles('error-icon')
						.is({visible: showError})
						.toString()}
				/>
				{icon && !error && <div className={fieldStyles('icon')}>{icon}</div>}
			</div>
			<AnimateHeight height={showError ? 'auto' : 0}>
				<div class={fieldStyles('error')}>
					<ErrorMessage name={name} />
				</div>
			</AnimateHeight>
		</div>
	)
}

export const FormfieldFile: FunctionComponent<
	DOMAttrs & {buttonText: string}
> = ({label, name, buttonText, placeholder, ...attrs}) => {
	const [field, meta, helpers] = useField(name)
	const showError = !!meta.error
	return (
		<div class={fieldStyles.mod('file')}>
			<label class={fieldStyles('input').is({error: showError})}>
				<input
					type="file"
					name={name}
					id={name}
					onChange={(e) => {
						helpers.setValue(e.currentTarget.files[0])
					}}
					{...attrs}
				/>
				<div class={fieldStyles('input-value')}>
					{field && field.value ? field.value.name : placeholder}
				</div>
			</label>
			<label htmlFor={name} class={fieldStyles('filebutton')}>
				{label}
			</label>
			<AnimateHeight height={showError ? 'auto' : 0}>
				<div class={fieldStyles('error')}>
					<ErrorMessage name={name} />
				</div>
			</AnimateHeight>
		</div>
	)
}

export const FormfieldCheckbox: FunctionComponent<DOMAttrs> = ({
	children,
	error,
	disabled,
	small = false,
	checked = false,
	name,
	...attrs
}) => {
	const [field, meta, helpers] = useField(name)
	const showError = !!meta.error

	return (
		<div
			class={fieldStyles
				.mod(['checkbox', small && 'small'])
				.is({error: showError, disabled})}
			onClick={(e) => {
				e.preventDefault()
				helpers.setValue(!field.value)
			}}
		>
			<label class={fieldStyles('label')}>
				<span class={fieldStyles('checker')}>
					<SuccessIcon
						className={fieldStyles('checker-icon')
							.is({checked: field.value})
							.toString()}
						isChecked={field.value}
					/>
				</span>
				<Field
					class={fieldStyles('input')}
					type="checkbox"
					name={name}
					id={name}
					{...attrs}
				/>
				{children}
			</label>
		</div>
	)
}

export const FormfieldSelect: React.FC<
	FieldType & {
		label?: string
		groups: Array<{key: string; value: string}>
		keyToData: (key: string) => void
	}
> = (data) => {
	const {groups, label, name, error, keyToData, ...attrs} = data
	const [field, meta, helpers] = useField(name)

	return (
		<div class={fieldStyles.mod('radiogroup')}>
			<div class={fieldStyles('container')}>
				{label && <div class={fieldStyles('label').mod('row')}>{label}</div>}
				{groups.map((group, index) => {
					const key = group.key
					const value = group.value
					const isChecked = key === field.value
					return (
						<label
							class={fieldStyles('radio')
								.mod(label && index === 0 && 'label')
								.is({error: !!meta.error})}
						>
							<div
								class={fieldStyles('radio-select').is({checked: isChecked})}
							/>
							<Field
								name={name}
								label={value}
								type="radio"
								value={key}
								{...attrs}
								checked={isChecked}
								onClick={(e) => {
									e.preventDefault()
									helpers.setValue(key)
									keyToData(key)
								}}
							/>
							<span class={fieldStyles('radio-text')}>{value}</span>
						</label>
					)
				})}
			</div>
		</div>
	)
}

export const FormFieldAutoCompleteItem: FunctionComponent<{
	label: string
	isHighlighted: boolean
}> = ({label, isHighlighted}) => (
	<div class={fieldStyles.autocompleteitem.is({highlighted: isHighlighted})}>
		{label}
	</div>
)

export const FormFieldMasked: FunctionComponent<DOMAttrs> = IMaskMixin(
	({inputRef, updateValue, value, ...props}) => (
		<Field
			{...props}
			onChange={updateValue}
			value={value}
			innerRef={inputRef}
		/>
	)
)

export const FormFieldHourSelector: FunctionComponent<
	DOMAttrs & {
		label?: string
		name: string
		small?: boolean
	}
> = ({label, name, small = false, ...attrs}) => {
	const [field, meta, helpers] = useField(name)
	const showError = !!meta.error
	const element = useRef<HTMLDivElement>()
	useEffect(() => {
		if (showError) scrollTo(() => 0, element.current)
	}, [showError])
	return (
		<div class={fieldStyles} ref={element}>
			<div class={fieldStyles('container')}>
				{label && <div class={fieldStyles('label')}>{label}</div>}
				<div class={fieldStyles('input').mod({small, hourselector: true})}>
					<FormFieldMasked
						error={showError}
						class={fieldStyles('input-value')}
						name={name}
						unMask={true}
						mask={'Hh:Mm'}
						radix="."
						placeholderChar="-"
						lazy={false}
						value={field.value}
						onInput={(e) => {
							const value: string = e.target.value
							const timeDigits: Array<string> = value.split(':')
							const hourDigits: string = timeDigits[0]
							const firstHourDigit: string = hourDigits[0]
							const firstHourDigitAsNumber: number = parseInt(
								firstHourDigit,
								10
							)
							if (
								!isNaN(firstHourDigitAsNumber) &&
								firstHourDigitAsNumber > 2
							) {
								if (timeDigits[1])
									e.target.value = `0${firstHourDigitAsNumber}:${timeDigits[1]}`
								else e.target.value = `0${firstHourDigitAsNumber}:`
							}
						}}
						onAccept={(value, mask) => {
							helpers.setValue(value)
						}}
						definitions={{
							H: /[0-9]/,
							h: /[0-9]/,
							M: /[0-5]/,
							m: /[0-9]/
						}}
						id={name}
						{...attrs}
					/>
				</div>
			</div>
			<AnimateHeight height={showError ? 'auto' : 0}>
				<div class={fieldStyles('error')}>
					<ErrorMessage name={name} />
				</div>
			</AnimateHeight>
		</div>
	)
}
